import { useMemo, useState, useEffect, useCallback } from "react";
import useTranslation from "@/hooks/use_translation";

import { SectionHeader, Body } from "@/sprout/typography";
import Container from "@/components/ui/container";
import { SearchBar } from "@components/job_search/search_bar.tsx";
import { SingleSelectFilter } from "@components/job_search/filters/single_select_filter.tsx";
import { MultiSelectFilter } from "@components/job_search/filters/multi_select_filter.tsx";
import { router } from "@inertiajs/react";
import {
  useDatePostedOptions,
  useEmploymentTypeOptions,
  useSalaryOptions,
  useWorkTypeOptions,
} from "@hooks/job_search/use_filter_options";
import { useEffectAfterMount } from "@hooks/use_effect_after_mount.ts";
import JobSearchTable from "@components/job_search/table.tsx";
import useDevice from "@hooks/use_device.ts";
import HelpPopover from "@sprout/help_popover.tsx";
import { TertiaryButton } from "@sprout/buttons";
import { SnowplowEvent, useSnowplowTracker } from "@hooks/use_tracker.ts";
import { type Location } from "@components/ui/location_input.tsx";

export type JobPost = {
  id: string;
  title: string;
  companyName: string;
  logoUrl: string;
  publicUrl: string;
  firstPublished: string;
  locations: string[];
  workType: string | null;
  payRanges: string;
  viewed: boolean;
};

export type SearchParams = {
  query?: string;
  location?: string;
  lat?: number;
  lon?: number;
  location_type?: string;
  country_short_name?: string;
  state_short_name?: string;
  date_posted?: string;
  salary?: string;
  work_type?: string[];
  employment_type?: string[];
};

type Props = {
  jobPosts: JobPost[] | undefined;
  moreResultsAvailable: boolean;
  page: number;
  browsing: boolean;
  defaultJobFiltersEnabled: boolean;
  trackingData: { [key: string]: string };
  granularLocationSearchEnabled: boolean;
};

const buildLocation = (urlParams: URLSearchParams): Location | null => {
  if (!urlParams.get("location") || !urlParams.get("lat") || !urlParams.get("lon")) {
    return null;
  }

  return {
    display_name: urlParams.get("location") || "",
    latitude: Number(urlParams.get("lat")) || 0,
    longitude: Number(urlParams.get("lon")) || 0,
    country_short_name: urlParams.get("country_short_name") || null,
    state_short_name: urlParams.get("state_short_name") || null,
    location_type: urlParams.get("location_type") || null,
  };
};

const JobSearch = ({
  jobPosts,
  moreResultsAvailable,
  page,
  browsing,
  defaultJobFiltersEnabled,
  trackingData,
  granularLocationSearchEnabled,
}: Props) => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const [location, setLocation] = useState<Location | null>(buildLocation(urlSearchParams));
  const [query, setQuery] = useState(urlSearchParams.get("query") || "");
  const tracker = useSnowplowTracker();

  const { t } = useTranslation("jobSearch");
  const device = useDevice();
  const [loading, setLoading] = useState(false);
  const [datePosted, setDatePosted] = useState(urlSearchParams.get("date_posted") || "");
  const [salary, setSalary] = useState(urlSearchParams.get("salary") || "");
  const [workType, setWorkType] = useState<string[]>(urlSearchParams.getAll("work_type[]"));
  const [employmentType, setEmploymentType] = useState<string[]>(
    urlSearchParams.getAll("employment_type[]"),
  );
  const isBrowsing = (typeof browsing === "undefined" && !query) || browsing;
  const browseKey = defaultJobFiltersEnabled ? "browseWithDefaults" : "browse";
  const resultsKey = defaultJobFiltersEnabled ? "resultsWithDefaults" : "results";
  const [searchResultsText, setSearchResultsText] = useState<string>(
    isBrowsing ? t(browseKey) : t(resultsKey),
  );
  const [searchSubtext, setSearchSubtext] = useState<string>(
    defaultJobFiltersEnabled && isBrowsing ? t("browseSubtext") : "",
  );
  const [noResultsMessage, setNoResultsMessage] = useState<string>("");

  const datePostedOptions = useDatePostedOptions();
  const salaryOptions = useSalaryOptions();
  const workTypeOptions = useWorkTypeOptions();
  const employmentTypeOptions = useEmploymentTypeOptions();

  useEffect(() => {
    if (typeof jobPosts === "undefined") return;

    if (jobPosts.length > 0) {
      if (browsing) {
        setSearchResultsText(t(browseKey));
        setSearchSubtext(defaultJobFiltersEnabled ? t("browseSubtext") : "");
      } else {
        setSearchResultsText(t(resultsKey));
        setSearchSubtext("");
      }
      return;
    }

    if (query) {
      setSearchResultsText(t("noResultsForSearchTerm", { searchTerm: query }));
      setNoResultsMessage(t("noResultsForSearchTermMessage"));
      setSearchSubtext("");
      return;
    }

    setSearchResultsText(t("noResults"));
    setNoResultsMessage(t("noResultsMessage"));
    setSearchSubtext("");
  }, [jobPosts, browsing]);

  const resetFilters = () => {
    setDatePosted("");
    setSalary("");
    setWorkType([]);
    setEmploymentType([]);
  };

  const hasActiveFilters = useMemo(
    () => datePosted !== "" || salary !== "" || workType.length > 0 || employmentType.length > 0,
    [datePosted, salary, workType, employmentType],
  );

  const searchParams: SearchParams = {
    query: query || undefined,
    location: location?.display_name,
    lat: location?.latitude,
    lon: location?.longitude,
    location_type: location?.location_type || undefined,
    country_short_name: location?.country_short_name || undefined,
    state_short_name: location?.state_short_name || undefined,
    date_posted: datePosted || undefined,
    salary: salary || undefined,
    work_type: workType || undefined,
    employment_type: employmentType || undefined,
  };

  const handleSearch = () => {
    setLoading(true);
    jobPosts = undefined;

    router.get("/jobs", searchParams, {
      onFinish: () => setLoading(false),
    });
  };

  useEffectAfterMount(() => {
    handleSearch();
  }, [datePosted, salary, workType, employmentType]);

  const createTrackSearchResultClick = useCallback(
    (postId: string, title: string) => {
      return () => {
        tracker.trackEvent(SnowplowEvent.SearchResultClicked, {
          job_post_id: postId,
          job_post_title: title,
          ...trackingData,
        });
      };
    },
    [trackingData],
  );

  const handleJobView = (jobPostId: string) => {
    if (!jobPosts) return;

    const updatedJobPosts = jobPosts.map((post) =>
      post.id === jobPostId ? { ...post, viewed: true } : post,
    );

    router.replaceProp("jobPosts", updatedJobPosts);
  };

  return (
    <>
      <div className="py-9 pl-7 pr-6 bg-green-100">
        <SearchBar
          onSearch={handleSearch}
          query={query}
          onQueryChange={setQuery}
          location={location}
          onLocationChange={setLocation}
          disabled={loading}
          granularLocationSearchEnabled={granularLocationSearchEnabled}
        />
      </div>
      <div className="flex gap-3 mt-6 mb-3 pb-2 px-3 mx-auto text-nowrap overflow-y-auto hide-mobile-scrollbar max-w-fit items-center">
        <SingleSelectFilter
          name={t("datePosted")}
          options={datePostedOptions}
          value={datePosted}
          onValueChange={setDatePosted}
        />
        <SingleSelectFilter
          name={t("salary")}
          options={salaryOptions}
          value={salary}
          onValueChange={setSalary}
        />
        <MultiSelectFilter
          name={t("workType")}
          options={workTypeOptions}
          values={workType}
          onValuesChange={setWorkType}
        />
        <MultiSelectFilter
          name={t("employmentType")}
          options={employmentTypeOptions}
          values={employmentType}
          onValuesChange={setEmploymentType}
        />
        {hasActiveFilters && (
          <TertiaryButton disabled={!hasActiveFilters} secondary onClick={resetFilters}>
            {t("resetFilters")}
          </TertiaryButton>
        )}
      </div>
      <Container className="!max-w-[1550px] !pt-0">
        <div className="flex flex-row items-center gap-2">
          <SectionHeader
            className={
              defaultJobFiltersEnabled ? "!text-[20px] !leading-[24px] !font-medium !pt-6" : ""
            }
          >
            {searchResultsText}
          </SectionHeader>
          {device.isMobile && <HelpPopover text={t("aiDisclaimer")} />}
        </div>
        {searchSubtext && <Body className="!text-black">{searchSubtext}</Body>}
        <JobSearchTable
          jobPosts={jobPosts}
          browsing={browsing}
          moreResultsAvailable={moreResultsAvailable}
          currentPage={page}
          lastPublishDate={jobPosts?.at(-1)?.firstPublished || null}
          emptyStateMessage={noResultsMessage}
          searchParams={searchParams}
          createTrackSearchResultClick={createTrackSearchResultClick}
          onView={handleJobView}
        />
      </Container>
    </>
  );
};

export default JobSearch;
